import {useState, useEffect, type ReactNode} from 'react'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {DetailField} from '@/components/shared/DetailField'
import {MutedText} from '@/components/shared/MutedText'
import {Stack} from '@/components/shared/Stack'
import {ErrorText} from '@/components/shared/ErrorText'

export interface InlineSelectOption {
    value: string
    label: string
    testId?: string
}

interface InlineSelectFieldProps {
    label: ReactNode
    value: string
    options: InlineSelectOption[]
    canEdit: boolean
    onChange: (newValue: string) => Promise<void>
    readOnlyValue?: ReactNode
    fullWidth?: boolean
    testId?: string
    helpText?: ReactNode
    'aria-label'?: string
}

export function InlineSelectField({
    label,
    value,
    options,
    canEdit,
    onChange,
    readOnlyValue,
    fullWidth,
    testId,
    helpText,
    'aria-label': ariaLabel,
}: InlineSelectFieldProps) {
    const [localValue, setLocalValue] = useState(value)
    const [error, setError] = useState<string | null>(null)
    const [pending, setPending] = useState(false)

    useEffect(() => {
        setLocalValue(value)
    }, [value])

    if (!canEdit) {
        const display = readOnlyValue
            ?? options.find(o => o.value === value)?.label
            ?? value
        return (
            <DetailField
                label={label}
                value={display}
                fullWidth={fullWidth}
            />
        )
    }

    const handleChange = async (next: string) => {
        setLocalValue(next)
        setError(null)
        setPending(true)
        try {
            await onChange(next)
        } catch (err) {
            setLocalValue(value)
            const msg = err instanceof Error ? err.message : String(err)
            setError(msg)
        } finally {
            setPending(false)
        }
    }

    return (
        <DetailField
            label={label}
            fullWidth={fullWidth}
            value={
                <Stack gap="xs">
                    <Select value={localValue} onValueChange={handleChange} disabled={pending}>
                        <SelectTrigger data-testid={testId} aria-label={ariaLabel}>
                            <SelectValue/>
                        </SelectTrigger>
                        <SelectContent>
                            {options.map(opt => (
                                <SelectItem
                                    key={opt.value}
                                    value={opt.value}
                                    data-testid={opt.testId}
                                >
                                    {opt.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {error && (
                        <ErrorText data-testid={testId ? `${testId}-error` : undefined}>
                            {error}
                        </ErrorText>
                    )}
                    {helpText && <MutedText>{helpText}</MutedText>}
                </Stack>
            }
        />
    )
}
